import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import { t } from 'i18next';

import {
  AddCareNote,
  CareNotesTable,
} from '@views/pages/Patient/components/CareNotes/components';
import { theme } from '@store';

import { paths } from '@common/types';
import { useCareNotes } from './hooks';
import { Select } from 'ui';
import { ALL_OPTION } from '@common/types/constants';
import { EditNote } from '@mui/icons-material';

interface ICareNotesProps {
  patient: paths[`/patient/{patient_id}`]['get']['responses']['200']['content']['application/json'];
  isEditDisabled: boolean;
  tabPanelInstrumentHeight: number;
}

export const CareNotes = ({
  patient,
  isEditDisabled = false,
  tabPanelInstrumentHeight,
}: ICareNotesProps) => {
  const {
    entries,
    count,
    filters,
    setFilters,
    notesTypeOptions,
    programOptions,
    providerOptions,
    addedByOptions,
    handleChangeFilterNoteType,
    handleChangeFilterProgram,
    handleChangeFilterProvider,
    handleChangeFilterAddedBy,
    handleResetFilter,
    isFilterDisabled,
    isLoading,
    isInitialFilter,
  } = useCareNotes(patient.id);

  const columns = [
    {
      id: 'program',
      label: t('patient.careNotes.table.headers.program'),
      sortable: true,
      width: 80,
    },
    {
      id: 'note_type',
      label: t('patient.careNotes.table.headers.note_type'),
      sortable: true,
      width: 100,
    },
    {
      id: 'recorded_minutes',
      label: t('patient.careNotes.table.headers.recorded_minutes'),
      sortable: true,
      width: 90,
    },
    {
      id: 'provider',
      label: t('patient.careNotes.table.headers.provider'),
      sortable: true,
      width: 90,
    },
    {
      id: 'created_by',
      label: t('patient.careNotes.table.headers.created_by_name'),
      sortable: true,
      width: 130,
    },
    {
      id: 'last_updated_at',
      label: t('patient.careNotes.table.headers.last_updated_at'),
      sortable: true,
      width: 110,
    },
    {
      id: 'notable',
      label: '',
      sortable: false,
      width: 50,
    },
    {
      id: 'action',
      label: '',
      sortable: false,
      width: 50,
    },
  ];

  return (
    <>
      {isInitialFilter && entries.length === 0 && !isLoading ? (
        <Box
          sx={{
            height: 'calc(100vh - 310px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <EditNote sx={{ width: 64, height: 64, mb: 2 }} color='primary' />
          <Typography variant='h6'>
            {t('patient.careNotes.table.noResults.title')}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.disabled' }}>
            {t('patient.careNotes.table.noResults.subTitle')}
          </Typography>
          <Box>
            <AddCareNote
              patientId={patient.id}
              variant='create'
              disabled={isEditDisabled || patient.account_status === 'Inactive'}
            />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: theme.palette.common.white,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              padding: 2,
              justifyContent: 'space-between',
              alignItems: 'center',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderBottom: 'none',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '70%',
              }}
            >
              <Select
                sx={{ maxWidth: '350px', minWidth: '25%' }}
                name='care-notes-type'
                size='medium'
                label={t('patient.careNotes.filters.noteType')}
                value={filters.note_type}
                onChange={handleChangeFilterNoteType}
                options={notesTypeOptions}
              />
              <Select
                sx={{ maxWidth: '350px', minWidth: '25%' }}
                name='care-notes-program'
                size='medium'
                label={t('patient.careNotes.filters.program')}
                value={filters.program_id}
                onChange={handleChangeFilterProgram}
                options={programOptions}
              />
              <Select
                sx={{ maxWidth: '350px', minWidth: '25%' }}
                name='care-notes-provider'
                size='medium'
                label={t('patient.careNotes.filters.provider')}
                value={filters.provider_id}
                onChange={handleChangeFilterProvider}
                options={providerOptions}
              />
              <Autocomplete
                disablePortal
                id='care-notes-added-by'
                options={addedByOptions}
                sx={{ maxWidth: '350px', minWidth: '25%' }}
                onChange={(e, newValue) => handleChangeFilterAddedBy(newValue)}
                value={addedByOptions.find(
                  user => user.value === filters.user_id,
                )}
                disableClearable={filters.user_id === ALL_OPTION.value}
                renderOption={(liProps, option, { index }) => (
                  <li {...liProps} key={option.value || `opt-${index}`}>
                    {option.label}
                  </li>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('patient.careNotes.filters.addedBy')}
                  />
                )}
              />
              <Button onClick={handleResetFilter} disabled={isFilterDisabled}>
                {t('patient.careNotes.filters.reset')}
              </Button>
            </Box>
            <Box>
              <AddCareNote
                patientId={patient.id}
                variant='create'
                disabled={
                  isEditDisabled || patient.account_status === 'Inactive'
                }
              />
            </Box>
          </Box>
          <CareNotesTable
            tabPanelInstrumentHeight={tabPanelInstrumentHeight}
            rows={entries}
            filters={filters}
            columns={columns}
            setFilters={setFilters}
            totalCount={count}
            loading={isLoading}
            patient_id={patient.id}
            disabled={isEditDisabled}
          />
        </Box>
      )}
    </>
  );
};
